import * as React from 'react';
import { useEffect, useState } from 'react';
import { generateUrl } from '../../../Utilities/api';
function scoreToBand(score) {
    if (score < 40)
        return 'red';
    if (score < 70)
        return 'yellow';
    return 'green';
}
// ── Constants ──────────────────────────────────────────────────────────────
const BRAND = {
    blue: '#00537e',
    blueDeep: '#004360',
    tint: '#EEF4F7',
    red: '#f93f43',
    yellow: '#efe300',
    green: '#138f38',
};
const BAND_COLORS = { red: BRAND.red, yellow: BRAND.yellow, green: BRAND.green };
const CATEGORY_LABELS = {
    kraft: 'Kraft', beweglichkeit: 'Beweglichkeit', gleichgewicht: 'Gleichgewicht',
    koerperzusammensetzung: 'Körperzusammensetzung', schmerzfreiheit: 'Schmerzfreiheit',
    stressfreiheit: 'Stressfreiheit', energie: 'Energie', ernaehrung: 'Ernährung',
    mental: 'Mental', environment: 'Umfeld', regeneration: 'Regeneration', training: 'Training',
};
const SOURCE_LABELS = {
    questionnaire: 'Fragebogen',
    trainer: 'Trainermessung',
};
function formatDate(iso) {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
// ── Score Ring ─────────────────────────────────────────────────────────────
function ScoreRing({ score, size = 52 }) {
    const sw = 5;
    const band = scoreToBand(score);
    const color = BAND_COLORS[band];
    const r = (size - sw) / 2;
    const circ = 2 * Math.PI * r;
    const dash = (score / 100) * circ;
    return (<svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={sw}/>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={sw} strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round"/>
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" fontSize={size * 0.26} fontWeight="700" fill="#111827" style={{ transform: 'rotate(90deg)', transformOrigin: '50% 50%' }}>
                {score}
            </text>
        </svg>);
}
const HealthscoreReportHistory = ({ detailBaseUrl }) => {
    const [reports, setReports] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        void fetch(generateUrl('sitzkrieger_v2_myareaapi_healthscore_v2_reports'), { credentials: 'same-origin' })
            .then(r => r.json())
            .then((data) => { setReports(data); setIsLoading(false); })
            .catch(() => setIsLoading(false));
    }, []);
    if (isLoading) {
        return (<div className="flex flex-col gap-3 animate-pulse">
                {[1, 2, 3].map(i => <div key={i} className="h-20 bg-gray-100 rounded-xl"/>)}
            </div>);
    }
    if (reports.length === 0) {
        return (<div className="flex flex-col items-center justify-center py-16 gap-3">
                <p className="text-gray-500 text-center max-w-xs">
                    Es wurden noch keine Berichte von deinem Trainer erstellt.
                </p>
            </div>);
    }
    return (<div className="flex flex-col gap-3">
            {reports.map((r, idx) => {
            const sources = r.dataSources.map(s => SOURCE_LABELS[s] ?? s).join(' + ');
            return (<a key={r.uuid} href={`${detailBaseUrl}/${r.uuid}`} className="bg-white rounded-2xl p-4 flex items-center gap-4 hover:shadow-lg transition-shadow" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)', borderLeft: idx === 0 ? `4px solid ${BRAND.blue}` : '4px solid transparent' }}>
                        <ScoreRing score={r.overallScore} size={56}/>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-sm font-semibold" style={{ color: BRAND.blueDeep }}>
                                    Score {r.overallScore}
                                </span>
                                {idx === 0 && (<span className="text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: BRAND.tint, color: BRAND.blue }}>Aktuell</span>)}
                            </div>
                            <p className="text-xs text-gray-400">{formatDate(r.generatedAt)}</p>
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {sources !== '' && (<span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{sources}</span>)}
                                {r.leverCategory !== null && (<span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                                        Hebel: {CATEGORY_LABELS[r.leverCategory] ?? r.leverCategory}
                                    </span>)}
                                {r.focusCategories.map(fc => (<span key={fc} className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: BRAND.tint, color: BRAND.blue }}>
                                        Fokus: {CATEGORY_LABELS[fc] ?? fc}
                                    </span>))}
                            </div>
                        </div>
                        <span className="text-gray-300 text-lg shrink-0">›</span>
                    </a>);
        })}
        </div>);
};
HealthscoreReportHistory.displayName = 'HealthscoreReportHistory';
export default HealthscoreReportHistory;
